import { ReactNode, useMemo } from 'react';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import * as Sentry from '@sentry/react';

type SentryProviderProps = {
  children: ReactNode;
};

export const SentryProvider = ({ children }: SentryProviderProps) => {
  Sentry.init({
    dsn: import.meta.env.VITE_APP_SENTRY_DSN,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 1.0,
    sendDefaultPii: true,
    environment: import.meta.env.VITE_APP_ENV
  });

  const fallback = useMemo(
    () => (
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: (theme) => theme.palette.background.default
        }}
      >
        <Paper elevation={3} sx={{ p: 4, maxWidth: 540, mx: 2 }}>
          <Stack spacing={2}>
            <Typography variant='h5' fontWeight={700}>
              Something went wrong
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              We’re sorry for the inconvenience. Our team has been notified of this error. You can try reloading the
              page or go back to the previous screen.
            </Typography>
            <Stack direction='row' spacing={1} justifyContent='flex-end'>
              <Button variant='contained' color='primary' onClick={() => window.location.reload()}>
                Reload Page
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Box>
    ),
    []
  );

  return (
    <Sentry.ErrorBoundary
      onError={(error) => {
        console.log('Sentry Error -->', error);
      }}
      fallback={fallback}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};
